import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";

import TransactionService from "../servics/notification/transaction/TransactionService";
import { formatAmount } from "../constants/marketplaceCheckout";
import { DEFAULT_CURRENCY } from "../config/countries";
import { subCategoryLabel } from "../constants/subCategoryLabels";

/** Identifiant de l'utilisateur : profil chargé en store, sinon user_id du JWT. */
const resolveUserId = (tenantUser) => {
    if (tenantUser?.id) return String(tenantUser.id);
    try {
        const token = localStorage.getItem("token");
        const userId = token ? jwtDecode(token)?.user_id : null;
        return userId ? String(userId) : null;
    } catch {
        return null;
    }
};

/** Accords refusés, expirés ou annulés : aucune vente ni achat n'en résulte. */
export const CLOSED_WITHOUT_SALE = ["REJECTED", "EXPIRED", "CANCELLED"];

export const isPaid = (transaction) => transaction.latestPaymentStatus === "SUCCEEDED";

export const isInProgress = (transaction) =>
    !CLOSED_WITHOUT_SALE.includes(transaction.agreementStatus) && !isPaid(transaction);

/** Montant d'une transaction : part produit pour une vente, total payé (livraison incluse) pour un achat. */
export const transactionAmount = (transaction) =>
    transaction.role === "SALE"
        ? transaction.productAmount
        : transaction.totalAmount ?? transaction.productAmount;

/** « Céréales · MAIZE » ; « Produit » si le catalogue ne le connaît plus. */
export const productLabel = (transaction) => {
    const subCategory = subCategoryLabel(transaction.subCategoryName);
    const label = subCategory ? subCategory.charAt(0).toUpperCase() + subCategory.slice(1) : null;
    return [label, transaction.productName].filter(Boolean).join(" · ") || "Produit";
};

/**
 * Somme par devise, formatée : « 38 000 F CFA » ou « 0 F CFA » si la liste est vide.
 * `fallbackCurrency` (devise de contexte) sert aux transactions sans devise et à la liste vide.
 */
export const formatTotals = (transactions, amountOf = transactionAmount, fallbackCurrency = DEFAULT_CURRENCY) => {
    const totals = {};
    transactions.forEach((transaction) => {
        const amount = Number(amountOf(transaction));
        if (!Number.isFinite(amount)) return;
        const currency = transaction.currency || fallbackCurrency;
        totals[currency] = (totals[currency] || 0) + amount;
    });
    const entries = Object.entries(totals);
    return entries.length === 0
        ? formatAmount(0, fallbackCurrency)
        : entries.map(([currency, amount]) => formatAmount(amount, currency)).join(" · ");
};

const byNewest = (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0);

/**
 * Transactions de l'utilisateur connecté (GET /marketplace/purchases), classées
 * en ventes (il est vendeur) et achats (il est acheteur), de la plus récente à la plus ancienne.
 * En cas d'échec, les listes restent vides et `error` est renseigné.
 */
const useMyTransactions = () => {
    const tenantUser = useSelector((state) => state.tenantUser.tenantUser);
    const userId = resolveUserId(tenantUser);

    const [state, setState] = useState({ loading: true, error: null, transactions: [] });

    useEffect(() => {
        let cancelled = false;
        setState((previous) => ({ ...previous, loading: true }));
        TransactionService.getPurchases()
            .then((data) => {
                if (!cancelled) setState({ loading: false, error: null, transactions: Array.isArray(data) ? data : [] });
            })
            .catch((error) => {
                console.warn("Transactions indisponibles", error);
                if (!cancelled) setState({ loading: false, error, transactions: [] });
            });
        return () => { cancelled = true; };
    }, [userId]);

    const transactions = state.transactions
        .map((transaction) => ({
            ...transaction,
            role: String(transaction.sellerId) === userId ? "SALE"
                : String(transaction.buyerId) === userId ? "PURCHASE" : null
        }))
        .filter((transaction) => transaction.role)
        .sort(byNewest);

    return {
        loading: state.loading,
        error: state.error,
        userId,
        transactions,
        sales: transactions.filter((transaction) => transaction.role === "SALE"),
        purchases: transactions.filter((transaction) => transaction.role === "PURCHASE")
    };
};

export default useMyTransactions;
